import React, { useState } from 'react';
import axios from 'axios';
import { biosdkOperationUrl, CONVERTER_SINGLE_API_BASE, logBiosdkUiRoute } from './config';
import { User, Upload, Loader2, AlertCircle, Clock, ArrowLeftRight } from 'lucide-react';
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Badge } from './components/ui/badge';
import { Label } from './components/ui/label';
import { Alert, AlertDescription, AlertTitle } from './components/ui/alert';
import { resolveIsMatch, parseOptionalNumber } from './matchResultUtils';

function MatcherInput() {
  const [sampleFile, setSampleFile] = useState(null);
  const [galleryFile, setGalleryFile] = useState(null);
  const [error, setError] = useState(null);
  const [matchResult, setMatchResult] = useState(null);
  const [sampleImageUrl, setSampleImageUrl] = useState(null);
  const [galleryImageUrl, setGalleryImageUrl] = useState(null);
  const [timing, setTiming] = useState({ convert1: 0, convert2: 0, convertSample: 0, convertGallery: 0, match: 0 });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [galleryExtracted, setGalleryExtracted] = useState(false);
  const [jp2isoSampleImageUrl, setjp2IsoSampleImageUrl] = useState(null);
  const [jp2isoGalleryImageUrl, setjp2IsoGalleryImageUrl] = useState(null);

  const toSignedByteJson = (arrayBuffer) => {
    const unsigned = new Uint8Array(arrayBuffer);
    const signed = Array.from(unsigned, (v) => (v > 127 ? v - 256 : v));
    return JSON.stringify(signed);
  };

  const resetState = () => {
    setMatchResult(null);
    setShowAdvanced(false);
    setError(null);
    setProcessing(false); 
  };

  const convertFaceImageToIso = async (inputFile) => {
    const formData = new FormData();
    formData.append('file', inputFile);
    return axios.post(`${CONVERTER_SINGLE_API_BASE}/convertImageFileToIso?modality=FACE`, formData, {
      responseType: 'arraybuffer',
    });
  };

  const handleSampleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    setSampleFile(selectedFile);
    resetState(); 

    if (!selectedFile) {
      setError('No file selected.');
      return;
    }

    const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
    let convertSampleDuration1 = 0;

    try {
      if (['jpeg', 'jpg', 'bmp', 'png'].includes(fileExtension)) {
        setSampleImageUrl(URL.createObjectURL(selectedFile));
      } else if (fileExtension === 'iso') {
        const isoFormData = new FormData();
        isoFormData.append('file', selectedFile);
        const response = await axios.post(`${CONVERTER_SINGLE_API_BASE}/convertFromIsoFile`, isoFormData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          responseType: 'arraybuffer',
        });
        const jpegBlob = new Blob([response.data], { type: 'image/jpeg' });
        setSampleImageUrl(URL.createObjectURL(jpegBlob));
        setjp2IsoSampleImageUrl(null);
      } else if (fileExtension === 'jp2') {
        setError('JP2 is not supported in direct mode. Use jpg/jpeg/png/bmp/iso.');
        setSampleFile(null);
        setSampleImageUrl(null);
        return;
      }
      setTiming((prevTiming) => ({
        ...prevTiming,
        convert1: convertSampleDuration1,
      }));
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Could not decode ISO for preview.';
      setError(msg);
      setSampleFile(null);
      setSampleImageUrl(null);
    }
  };

  const handleGalleryFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    setGalleryFile(selectedFile);
    resetState();

    if (!selectedFile) {
      setError('No file selected.');
      return;
    }

    const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
    let convertGalleryDuration1 = 0;

    try {
      if (galleryExtracted && fileExtension !== 'iso') {
        setError('When gallery is marked as extracted, upload only ISO for gallery.');
        setGalleryFile(null);
        setGalleryImageUrl(null);
        return;
      }
      if (['jpeg', 'jpg', 'bmp', 'png'].includes(fileExtension)) {
        setGalleryImageUrl(URL.createObjectURL(selectedFile));
      } else if (fileExtension === 'iso') {
        const isoFormData = new FormData();
        isoFormData.append('file', selectedFile);
        const response = await axios.post(`${CONVERTER_SINGLE_API_BASE}/convertFromIsoFile`, isoFormData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          responseType: 'arraybuffer',
        });
        const jpegBlob = new Blob([response.data], { type: 'image/jpeg' });
        setGalleryImageUrl(URL.createObjectURL(jpegBlob));
        setjp2IsoGalleryImageUrl(null);
      } else if (fileExtension === 'jp2') {
        setError('JP2 is not supported in direct mode. Use jpg/jpeg/png/bmp/iso.');
        setGalleryFile(null);
        setGalleryImageUrl(null);
        return;
      }
      setTiming((prevTiming) => ({
        ...prevTiming,
        convert2: convertGalleryDuration1,
      }));
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Could not decode ISO for preview.';
      setError(msg);
      setGalleryFile(null);
      setGalleryImageUrl(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!sampleFile || !galleryFile) {
      setError('Please upload both sample and gallery images.');
      return;
    }

    setProcessing(true);

    let f1 = [];
    let f2 = [];
    let convertSampleDuration = 0;
    let convertGalleryDuration = 0;
    try {
      const startConvertSample = performance.now();
      const sampleFileExtension = sampleFile.name.split('.').pop().toLowerCase();
      
      if (galleryExtracted) {
        if (['jpeg', 'jpg', 'bmp', 'png'].includes(sampleFileExtension)) {
          const isoResp = await convertFaceImageToIso(sampleFile);
          const endConvertSample = performance.now();
          convertSampleDuration = endConvertSample - startConvertSample;
          f1 = toSignedByteJson(isoResp.data);
        } else if (sampleFileExtension === 'iso') {
          const sampleIsoArrayBuffer = await sampleFile.arrayBuffer();
          const endConvertSample = performance.now();
          convertSampleDuration = endConvertSample - startConvertSample;
          f1 = toSignedByteJson(sampleIsoArrayBuffer);
        } else {
          throw new Error('Unsupported sample file type for extracted gallery. Use jpg/jpeg/png/bmp/iso.');
        }
      } else if (sampleFileExtension === 'jpeg' || sampleFileExtension === 'jpg'|| sampleFileExtension === 'bmp'|| sampleFileExtension === 'png' || sampleFileExtension === 'iso') {
        const sampleIsoArrayBuffer = await sampleFile.arrayBuffer();
        const endConvertSample = performance.now();
        convertSampleDuration = endConvertSample - startConvertSample;
        f1 = toSignedByteJson(sampleIsoArrayBuffer);
      } else {
        throw new Error('Unsupported sample file type. Use jpg/jpeg/png/bmp/iso.');
      }

      const startConvertGallery = performance.now();
      const galleryFileExtension = galleryFile.name.split('.').pop().toLowerCase();
      if (galleryExtracted && galleryFileExtension !== 'iso') {
        throw new Error('When gallery is marked as extracted, upload only ISO for gallery.');
      }
      if (galleryFileExtension === 'jpeg' || galleryFileExtension === 'jpg'|| galleryFileExtension === 'bmp'|| galleryFileExtension === 'png' || galleryFileExtension === 'iso') {
        const GalleryisoArrayBuffer = await galleryFile.arrayBuffer();
        const endConvertGallery = performance.now();
        convertGalleryDuration = endConvertGallery - startConvertGallery;
        f2 = toSignedByteJson(GalleryisoArrayBuffer);
      } else {
        throw new Error('Unsupported gallery file type. Use jpg/jpeg/png/bmp/iso.');
      }

      const templateResponse = await fetch('/face_match_inner_request-template.json');
      if (!templateResponse.ok) {
        throw new Error(`Template fetch failed with status: ${templateResponse.status}`);
      }

      let templateText = await templateResponse.text();
      templateText = templateText
        .replace('sample_bdb', f1)
        .replace('gallery_bdb', f2);

      const versionRequestTemplateResponse = await fetch('/match_request-template.json');
      if (!versionRequestTemplateResponse.ok) {
        throw new Error(`Version request template fetch failed with status: ${versionRequestTemplateResponse.status}`);
      }

      const versionRequestTemplateText = await versionRequestTemplateResponse.text();
      const base64EncodedTemplate = btoa(templateText);
      const finalRequestBody = JSON.parse(
        versionRequestTemplateText.replace('encoded_request', base64EncodedTemplate)
      );

      finalRequestBody.operation = 'match';
      const isRawInput = sampleFileExtension !== 'iso' || galleryFileExtension !== 'iso';
      const matchUrl = biosdkOperationUrl('process', isRawInput);
      logBiosdkUiRoute('FaceMatcher', isRawInput ? 'rawRoute' : 'isoRoute', matchUrl, {
        operation: 'process',
      });
      const matchResponse = await axios.post(matchUrl, finalRequestBody, {
        headers: { 'Content-Type': 'application/json' },
      });
      
      const statusCode = matchResponse?.data?.response?.statusCode;
      if (statusCode && statusCode !== 200) {
        throw new Error(matchResponse?.data?.response?.statusMessage || 'Face match failed');
      }

      const faceDecision =
        matchResponse?.data?.response?.response?.[0]?.decisions?.FACE ||
        matchResponse?.data?.response?.response?.[0]?.decisions?.[Object.keys(matchResponse?.data?.response?.response?.[0]?.decisions || {})[0]] ||
        {};
      const analytics = faceDecision.analyticsInfo || {};
      const matchDuration = Number(analytics.matchDuration || 0);

      if (matchResponse.data.errors && matchResponse.data.errors.length > 0) {
        setError(`Error: ${matchResponse.data.errors[0].message}`);
        setMatchResult(null);
      } else {
        const decision = faceDecision;
        const score = parseOptionalNumber(
          analytics.score ?? analytics.SCORE ?? decision.score ?? analytics.matchScore
        );
        const threshold = parseOptionalNumber(
          analytics.threshold ?? analytics.THRESHOLD ?? decision.threshold ?? analytics.matchThreshold
        );
        const isMatch = resolveIsMatch(score, threshold, decision.match, 'distance');

        setMatchResult({
          score,
          threshold,
          isMatch,
        });
        setError(null);
      }

      setTiming({
        convert1: timing.convert1,
        convert2: timing.convert2,
        convertSample: convertSampleDuration,
        convertGallery: convertGalleryDuration,
        match: matchDuration,
      });
      setShowAdvanced(true);

    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'An error occurred';
      setError(`Error: ${errorMessage}`);
      setMatchResult(null);
    } finally {
      setProcessing(false); 
    }
  };

  const handleReset = () => {
    setSampleFile(null);
    setGalleryFile(null);
    setSampleImageUrl(null);
    setGalleryImageUrl(null);
    setGalleryExtracted(false);
    setMatchResult(null);
    setError(null);
    setShowAdvanced(false);
  };

  const formatScore = (value) => {
    if (value === null || value === undefined) return 'N/A';
    const num = typeof value === 'number' ? value : parseFloat(value);
    if (Number.isNaN(num)) return 'N/A';
    return num.toFixed(2);
  };

  const getProgressWidth = (score, threshold) => {
    const scoreNum = typeof score === 'number' ? score : parseFloat(score);
    const thresholdNum = typeof threshold === 'number' ? threshold : parseFloat(threshold);
    if (Number.isNaN(scoreNum) || Number.isNaN(thresholdNum)) return 0;
    const maxValue = Math.max(100, thresholdNum * 1.5, scoreNum);
    return Math.min(100, (scoreNum / maxValue) * 100);
  };

  const ImageUploadCard = ({ title, file, preview, onFileChange, type }) => (
    <Card className="flex-1">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="upload-zone-small">
          <input
            type="file"
            id={`file-${type}`}
            accept={type === 'gallery' ? (galleryExtracted ? '.iso' : '.jpeg,.jpg,.iso,.bmp,.jp2,.png') : '.jpeg,.jpg,.iso,.bmp,.jp2,.png'}
            onChange={onFileChange}
            className="hidden"
          />
          <label htmlFor={`file-${type}`} className="cursor-pointer">
            {preview ? (
              <div className="relative">
                <img src={preview} alt={`${title} preview`} className="w-full h-32 object-contain rounded" />
                <Badge variant="face" className="absolute top-1 right-1 text-xs">Face</Badge>
              </div>
            ) : (
              <>
                <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                <p className="text-xs text-gray-600">Click to upload</p>
              </>
            )}
          </label>
        </div>
        {file && <p className="text-xs text-gray-500 text-center truncate">{file.name}</p>}
      </CardContent>
    </Card>
  );

  return (
    <div className="biochq-page">
      <div className="biochq-container-wide">
        <Card className="biochq-card face">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <User className="h-8 w-8 text-face" />
              <CardTitle className="text-3xl font-bold text-gray-800">Face Matcher</CardTitle>
            </div>
            <CardDescription className="text-gray-600">
              Compare two face images to verify identity using biometric matching
            </CardDescription>
          </CardHeader>

          <CardContent>
            <div className="space-y-6">
              <div className="max-w-md mx-auto">
                <div className="flex items-center gap-2 justify-center">
                  <input
                    type="checkbox"
                    id="faceGalleryExtracted"
                    checked={galleryExtracted}
                    onChange={(e) => {
                      setGalleryExtracted(e.target.checked);
                      setGalleryFile(null);
                      setGalleryImageUrl(null);
                      setjp2IsoGalleryImageUrl(null);
                      setMatchResult(null);
                      setError(null);
                    }}
                    className="rounded"
                  />
                  <Label htmlFor="faceGalleryExtracted" className="text-sm text-gray-600">
                    Gallery image is already extracted
                  </Label>
                </div>
              </div>
              {/* Image Upload Section */}
              <div className="flex gap-4 items-stretch">
                <ImageUploadCard
                  title="Sample Face"
                  file={sampleFile}
                  preview={sampleImageUrl}
                  onFileChange={handleSampleFileChange}
                  type="sample"
                />

                <div className="flex items-center justify-center">
                  <div className="compare-icon">
                    <ArrowLeftRight className="h-6 w-6 text-primary" />
                  </div>
                </div>

                <ImageUploadCard
                  title="Gallery Face"
                  file={galleryFile}
                  preview={galleryImageUrl}
                  onFileChange={handleGalleryFileChange}
                  type="gallery"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 justify-center">
                <Button
                  onClick={handleSubmit}
                  disabled={!sampleFile || !galleryFile || processing}
                  variant="face"
                  size="lg"
                >
                  {processing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Matching...
                    </>
                  ) : (
                    <>
                      <User className="mr-2 h-4 w-4" />
                      Match Faces
                    </>
                  )}
                </Button>
                <Button onClick={handleReset} variant="outline" size="lg" disabled={processing}>
                  Reset
                </Button>
              </div>

              {/* Error Display */}
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Match Result */}
              {matchResult && (
                <div className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    {/* Score Visualization */}
                    <Card className="bg-gray-50">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg">Match Score</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="relative pt-4">
                          <div className="flex mb-2 items-center justify-between">
                            <span className="text-xs font-semibold text-gray-600">0</span>
                            <span className="text-xs font-semibold text-gray-600">
                              {parseFloat(matchResult.score) > 100 ? formatScore(matchResult.score) : '100'}
                            </span>
                          </div>
                          <div className="overflow-hidden h-4 text-xs flex rounded-full bg-gray-200">
                            <div
                              style={{ width: `${getProgressWidth(matchResult.score, matchResult.threshold)}%` }}
                              className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center transition-all duration-500 ${
                                matchResult.isMatch ? 'bg-green-500' : 'bg-yellow-500'
                              }`}
                            />
                          </div>
                          <p className="text-center mt-3 text-2xl font-bold text-primary">
                            {formatScore(matchResult.score)}
                          </p>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Match Details */}
                    <Card className="bg-gray-50">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg">Match Details</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Score:</span>
                          <span className="font-medium">{formatScore(matchResult.score)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Threshold:</span>
                          <span className="font-medium">{formatScore(matchResult.threshold)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Match Status:</span>
                          <Badge variant={matchResult.isMatch ? 'success' : 'warning'}>
                            {matchResult.isMatch ? 'MATCHED' : 'NOT MATCHED'}
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Timing Details */}
                  {showAdvanced && (
                    <Card className="timing-card">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Clock className="h-4 w-4" />
                          Timing Details
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-3 gap-4 text-sm text-center">
                          <div>
                            <p className="text-gray-600">Sample Convert</p>
                            <p className="font-medium">{timing.convertSample.toFixed(2)} ms</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Gallery Convert</p>
                            <p className="font-medium">{timing.convertGallery.toFixed(2)} ms</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Match Time</p>
                            <p className="font-medium text-primary">{timing.match} ms</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default MatcherInput;
